import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";
import { GTA_GAME_TABS as GAMES } from "../components/ToolbarHeader";

const CHARACTER_TYPES = [
  "protagonist",
  "antagonist",
  "supporting",
  "npc",
  "custom",
];

const labelByKey = (k) =>
  GAMES.find((g) => g.key === k)?.label || "Other";

export default function CharacterStoryForm({
  user,
  authUser, // optional; falls back to `user`
  role,
  onDone,
  onCancel,
  showList = false,
}) {
  const currentUser = authUser ?? user;
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState({ type: null, text: "" });
  const [issues, setIssues] = useState([]);
  const storage = getStorage();
  const [form, setForm] = useState({
    characterName: "",
    characterType: "npc",
    gameKey: "gta-6",
    game: "GTA VI",

    synopsis: "",
    story: "",
    tags: "",
    quotes: "",
    imageFile: null,
    imageUrl: "",
    isPrivate: true,

    bio: {
      fullName: "",
      born: "",
      background: "",
      personality: "",
      skills: "",
      dreams: "",
      likes: "",
      dislikes: "",
      partner: "",
      children: "",
      status: "",
      affiliations: [],
      voiceActor: "",
    },
  });

  const fillDemoCharacter = () => {
    setForm({
      characterName: "Officer Maria Latore",
      characterType: "supporting",
      game: "GTA III",
      gameKey: "gta-3",
      imageUrl: "",

      bio: {
        fullName: "Maria Latore",
        born: "1970s, Liberty City, USA",
        background:
          "Maria Latore is the naïve and attention-seeking girlfriend of mafia boss Salvatore Leone. Despite being romantically involved with a powerful crime lord, Maria is largely unaware of the true danger surrounding her and frequently places herself at risk through reckless behavior.",
        personality:
          "Naïve, impulsive, dramatic, and emotionally dependent. Maria craves affection and excitement, often ignoring obvious threats around her.",
        skills:
          "Social manipulation, emotional influence, survival through connections.",
        dreams:
          "To live a glamorous life filled with love, excitement, and attention.",
        likes:
          "Attention, romance, luxury, nightlife.",
        dislikes:
          "Being ignored, boredom, responsibility.",
        partner: "Salvatore Leone",
        children: "None",
        status: "Alive",
        affiliations: [
          "Leone Crime Family"
        ],
        voiceActor: "Debi Mae West"
      },

      synopsis:
        "The reckless girlfriend of a mafia boss whose carelessness sparks deadly consequences.",

      story:
        "Maria Latore becomes entangled in Liberty City’s violent underworld through her relationship with Salvatore Leone. Her impulsive actions and flirtations raise Salvatore’s paranoia, ultimately leading him to believe Claude is a threat. Maria unknowingly plays a key role in triggering the betrayal that forces Claude to flee Portland and escalates the game’s conflict.",

      quotes: [
        "Salvatore’s just misunderstood!",
        "I just want someone to love me!",
        "Why does everyone always leave me?"
      ],

      tags: [
        "supporting",
        "gta 3",
        "maria latore",
        "leone family",
        "mafia"
      ],

      isPrivate: false,
      authorUid: "SYSTEM_CANON",
      roleAtCreation: "admin",
      createdAt: "SERVER_TIMESTAMP",
      updatedAt: "SERVER_TIMESTAMP"
    });
  };

  /* ---------------------------------- */
  /* Handlers */
  /* ---------------------------------- */

  const onChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imageFile") {
      setForm(f => ({ ...f, imageFile: files?.[0] || null }));
      return;
    }

    if (name.startsWith("bio.")) {
      const key = name.replace("bio.", "");
      setForm(f => ({
        ...f,
        bio: {
          ...f.bio,
          [key]: value,
        },
      }));
      return;
    }

    if (name === "gameKey") {
      setForm(f => ({
        ...f,
        gameKey: value,
        game: labelByKey(value),
      }));
      return;
    }

    setForm(f => ({ ...f, [name]: value }));
  };

  const uploadImageIfAny = async () => {
    if (!form.imageFile || !currentUser) return "";
    const path = `characters/${currentUser.uid}/${Date.now()}_${form.imageFile.name}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, form.imageFile);
    return getDownloadURL(storageRef);
  };
  const normalizeCsv = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(",")
        .map(v => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const csvValue = (value) =>
    Array.isArray(value) ? value.join(", ") : value || "";

  const validate = () => {
    const errs = [];
    if (!currentUser?.uid) errs.push("You must be signed in.");
    if (!form.characterName.trim()) errs.push("Character name is required.");
    if (!form.bio.fullName.trim()) errs.push("Full name is required.");
    if (!form.story.trim()) errs.push("Story is required.");
    return errs;
  };

  /* ---------------------------------- */
  /* Submit */
  /* ---------------------------------- */

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const errs = validate();
    setIssues(errs);
    if (errs.length) return;

    setSubmitting(true);

    try {
      const imageUrl = await uploadImageIfAny();

      const payload = {
        characterName: form.characterName.trim(),
        characterType: form.characterType,

        game: form.game,
        gameKey: form.gameKey,

        synopsis: form.synopsis.trim(),
        story: form.story.trim(),

        tags: normalizeCsv(form.tags),
        quotes: normalizeCsv(form.quotes),


        imageUrl,

        bio: form.bio,

        isPrivate: form.isPrivate,
        authorUid: currentUser.uid,
        roleAtCreation: role || "member",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, "characters"), payload);

      toast.success("Character saved successfully");
      onDone?.();
    } catch (err) {
      console.error(err);
      toast.error("Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const previewTags = normalizeCsv(form.tags);


  return (
    <>
      {banner.type && (
        <div
          className={
            "mt6-banner " +
            (banner.type === "ok"
              ? "mt6-banner--ok"
              : banner.type === "warn"
                ? "mt6-banner--warn"
                : "mt6-banner--error")
          }
        >
          {banner.text}
        </div>
      )}

      {issues.length > 0 && (
        <ul className="mt6-issues" role="alert">
          {issues.map((m, i) => (
            <li key={i}>• {m}</li>
          ))}
        </ul>
      )}
      <form onSubmit={onSubmit} className="mt6-form">
        {/* ---- BASICS ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Basics</h4>

          <div className="mt6-row-3">
            <div>
              <label className="mt6-label">Character Name *</label>
              <input
                className="mt6-input"
                name="characterName"
                value={form.characterName}
                onChange={onChange}
                placeholder="e.g. Lucia Caminos"
                required
                disabled={submitting}
              />

              <label className="mt6-label">Character Type</label>
              <select
                name="characterType"
                value={form.characterType}
                onChange={onChange}
                className="mt6-input"
              >
                {CHARACTER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>

              <label className="mt6-label">Game</label>
              <select
                name="gameKey"
                value={form.gameKey}
                onChange={onChange}
                className="mt6-input"
              >
                {GAMES.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mt6-label">Visibility</label>
              <div className="mt6-segment">
                <button
                  type="button"
                  className={`mt6-seg-btn ${!form.isPrivate ? "is-active" : ""}`}
                  onClick={() => setForm(f => ({ ...f, isPrivate: false }))}
                >
                  Public
                </button>
                <button
                  type="button"
                  className={`mt6-seg-btn ${form.isPrivate ? "is-active" : ""}`}
                  onClick={() => setForm(f => ({ ...f, isPrivate: true }))}
                >
                  Private
                </button>
              </div>

              <p className="mt6-muted">
                {form.isPrivate
                  ? "Only you can see this character."
                  : "Visible to everyone on the site."}
              </p>
            </div>
          </div>
        </div>


        {/* ---- BIO ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Character Bio</h4>

          <label className="mt6-label">Full Name *</label>
          <input
            className="mt6-input"
            name="bio.fullName"
            value={form.bio.fullName}
            onChange={onChange}
            placeholder="e.g. Michael Townley"
            required
          />

          <label className="mt6-label">Born</label>
          <input
            className="mt6-input"
            name="bio.born"
            value={form.bio.born}
            onChange={onChange}
            placeholder="e.g. 1965, Midwest United States"
          />

          <label className="mt6-label">Background</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="bio.background"
            value={form.bio.background}
            onChange={onChange}
            placeholder="Brief backstory and origins of the character..."
            rows={3}
          />

          <label className="mt6-label">Personality</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="bio.personality"
            value={form.bio.personality}
            onChange={onChange}
            placeholder="Traits, temperament, flaws, mindset..."
            rows={3}
          />

          <label className="mt6-label">Skills</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="bio.skills"
            value={form.bio.skills}
            onChange={onChange}
            placeholder="Weapons, driving, hacking, leadership, etc."
            rows={2}
          />

          <label className="mt6-label">Dreams / Goals</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="bio.dreams"
            value={form.bio.dreams}
            onChange={onChange}
            placeholder="What does this character want most?"
            rows={2}
          />

          <label className="mt6-label">Likes</label>
          <input
            className="mt6-input"
            name="bio.likes"
            value={form.bio.likes}
            onChange={onChange}
            placeholder="Cars, money, family, chaos..."
          />

          <label className="mt6-label">Partner</label>
          <input
            className="mt6-input"
            name="bio.partner"
            value={form.bio.partner}
            onChange={onChange}
            placeholder="Spouse / Partner name (if any)"
          />

          <label className="mt6-label">Children</label>
          <input
            className="mt6-input"
            name="bio.children"
            value={form.bio.children}
            onChange={onChange}
            placeholder="Names or 'None'"
          />
        </div>


        {/* ---- METADATA ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Metadata</h4>

          <label className="mt6-label">Tags</label>
          <input
            className="mt6-input"
            name="tags"
            value={csvValue(form.tags)}
            onChange={onChange}
            placeholder="crime, gta5, protagonist"
          />


          <label className="mt6-label">Quotes</label>
          <input
            className="mt6-input"
            name="quotes"
            value={csvValue(form.quotes)}
            onChange={onChange}
            placeholder="Famous quotes (comma separated)"
          />

        </div>


        {/* ---- STORY ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Story</h4>

          <label className="mt6-label">Short Synopsis</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="synopsis"
            value={form.synopsis}
            onChange={onChange}
            placeholder="One-paragraph summary of the character"
            rows={3}
          />

          <label className="mt6-label">Full Story *</label>
          <textarea
            className="mt6-input mt6-input--textarea"
            name="story"
            value={form.story}
            onChange={onChange}
            required
            rows={10}
            placeholder="Write the full character story here..."
          />
        </div>


        {/* ---- MEDIA ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Media</h4>

          <label className="mt6-label">Cover Image</label>
          <input
            type="file"
            name="imageFile"
            accept="image/*"
            onChange={onChange}
            className="mt6-input"
          />

          {form.imageUrl && (
            <img
              src={form.imageUrl}
              alt="Character preview"
              style={{ maxWidth: 240, borderRadius: 10, marginTop: 8 }}
            />
          )}
        </div>


        {/* ---- PREVIEW ---- */}
        <div className="mt6-section">
          <h4 className="mt6-h4">Preview</h4>

          <strong>{form.characterName}</strong>
          <div>{form.game}</div>

          {form.bio.fullName && (
            <div>
              <strong>Full Name:</strong> {form.bio.fullName}
            </div>
          )}
          {form.bio.background && <div>{form.bio.background}</div>}
        </div>

        {/* ---- ACTIONS ---- */}
        <div className="mt6-actions">
          <button
            className="mt6-btn mt6-btn--primary"
            type="submit"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>

          <button
            type="button"
            className="mt6-btn mt6-btn--ghost"
            onClick={fillDemoCharacter}
          >
            Load Sample Data
          </button>

          {onCancel && (
            <button
              type="button"
              className="mt6-btn mt6-btn--ghost"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

    </>
  );
}
